/**
 * Roles y permisos. La API autoriza por *permiso*, nunca por nombre de rol,
 * para que una organización pueda reorganizar sus roles
 * (settings.rolePermissions) sin cambios de código. Los valores por defecto
 * reflejan una IMF guatemalteca típica con segregación de funciones: quien
 * origina un préstamo no puede aprobarlo y quien recibe efectivo no puede
 * reversarlo.
 *
 * Contabilidad: gerencia y auditoría consultan (`accounting:read`); solo la
 * administración cierra períodos, corre ajustes y mapea el catálogo
 * (`accounting:manage`) y reabre un período cerrado (`accounting:reopen`),
 * porque reabrir altera cifras ya reportadas. La institución puede asignar
 * `accounting:manage` a un rol de contador desde Institución › Permisos.
 */
import { keyedEnum } from '../catalogs/enum.js';
/** Roles de usuario disponibles. */
export const ROLES = [
    'admin',
    'manager',
    'advisor',
    'appraiser',
    'analyst',
    'committee',
    'cashier',
    'collector',
    'compliance',
    'auditor',
];
/** Catálogo con clave de roles: `ROLE.admin`. */
export const ROLE = keyedEnum(ROLES);
/** Nombre en español de cada rol. */
export const ROLE_LABELS = {
    admin: 'Administrador',
    manager: 'Gerente de agencia',
    advisor: 'Asesor de crédito',
    appraiser: 'Valuador',
    analyst: 'Analista de crédito',
    committee: 'Comité de crédito',
    cashier: 'Cajero',
    collector: 'Gestor de cobros',
    compliance: 'Oficial de cumplimiento',
    auditor: 'Auditor interno',
};
/** Descripción en español de las funciones de cada rol. */
export const ROLE_DESCRIPTIONS = {
    admin: 'Configura la institución, productos, agencias y usuarios.',
    manager: 'Supervisa la agencia, desembolsa, autoriza reversas y cierres de caja.',
    advisor: 'Promueve, registra clientes y solicitudes, visita y da seguimiento a su cartera.',
    appraiser: 'Evalúa y valúa garantías prendarias e hipotecarias.',
    analyst: 'Realiza el estudio financiero, la verificación de campo y recomienda.',
    committee: 'Resuelve solicitudes: aprueba, modifica condiciones o rechaza.',
    cashier: 'Recibe pagos, emite recibos y opera la caja diaria.',
    collector: 'Gestiona la cartera en mora: llamadas, visitas y promesas de pago.',
    compliance: 'Supervisa alertas de lavado de dinero (IVE), listas y perfiles de clientes.',
    auditor: 'Consulta todo en modo lectura, incluida la bitácora de auditoría.',
};
/** Permisos granulares con formato `recurso:acción`. */
export const PERMISSIONS = [
    'clients:read',
    'clients:write',
    'clients:delete',
    'loans:read',
    'loans:originate',
    'loans:appraise',
    'loans:analyze',
    'loans:decide',
    'loans:disburse',
    'loans:cancel',
    'loans:write_off',
    'payments:read',
    'payments:post',
    'payments:reverse',
    'cash:operate',
    'cash:supervise',
    'collections:read',
    'collections:manage',
    'collections:assign',
    'reports:read',
    'reports:export',
    'billing:read',
    'billing:manage',
    'compliance:read',
    'compliance:manage',
    'catalog:manage',
    'employees:manage',
    'settings:manage',
    'audit:read',
    'branches:all',
    'accounting:read',
    'accounting:manage',
    'accounting:reopen',
];
/** Etiquetas en español de cada permiso. */
export const PERMISSION_LABELS = {
    'clients:read': 'Ver clientes',
    'clients:write': 'Registrar y editar clientes',
    'clients:delete': 'Dar de baja clientes',
    'loans:read': 'Ver préstamos',
    'loans:originate': 'Crear solicitudes y avanzar la originación',
    'loans:appraise': 'Valuar garantías',
    'loans:analyze': 'Estudio financiero y verificación de campo',
    'loans:decide': 'Resolver en comité',
    'loans:disburse': 'Desembolsar',
    'loans:cancel': 'Anular solicitudes',
    'loans:write_off': 'Castigar cartera',
    'payments:read': 'Ver pagos',
    'payments:post': 'Registrar pagos',
    'payments:reverse': 'Reversar pagos',
    'cash:operate': 'Operar caja',
    'cash:supervise': 'Supervisar cajas de la agencia',
    'collections:read': 'Ver cartera en mora',
    'collections:manage': 'Registrar gestiones de cobro',
    'collections:assign': 'Asignar cartera a gestores y reasignar préstamos',
    'reports:read': 'Ver reportes',
    'reports:export': 'Exportar reportes',
    'billing:read': 'Ver facturas electrónicas (FEL)',
    'billing:manage': 'Reintentar y descargar facturas electrónicas (FEL)',
    'compliance:read': 'Ver alertas de cumplimiento',
    'compliance:manage': 'Gestionar alertas de cumplimiento',
    'catalog:manage': 'Administrar productos y agencias',
    'employees:manage': 'Administrar usuarios',
    'settings:manage': 'Configurar la institución',
    'audit:read': 'Ver bitácora de auditoría',
    'branches:all': 'Ver todas las agencias',
    'accounting:read': 'Ver contabilidad (diario, balanza, mayor)',
    'accounting:manage': 'Cerrar períodos, ajustes de cierre y catálogo de cuentas',
    'accounting:reopen': 'Reabrir períodos contables cerrados',
};
/** Permisos de consulta general que comparten los roles de supervisión. */
const READ_ALL = [
    'clients:read',
    'loans:read',
    'payments:read',
    'collections:read',
    'reports:read',
    'branches:all',
];
/** Permisos por defecto de cada rol (la organización puede sobrescribirlos). */
export const DEFAULT_ROLE_PERMISSIONS = {
    admin: PERMISSIONS,
    manager: [
        ...READ_ALL.filter((p) => p !== 'branches:all'),
        'clients:write',
        'loans:originate',
        'loans:disburse',
        'loans:cancel',
        'payments:post',
        'payments:reverse',
        'cash:supervise',
        'collections:manage',
        'collections:assign',
        'reports:export',
        'compliance:read',
        'billing:read',
        'billing:manage',
        'accounting:read',
    ],
    advisor: ['clients:read', 'clients:write', 'loans:read', 'loans:originate', 'payments:read', 'collections:read', 'collections:manage'],
    appraiser: ['clients:read', 'loans:read', 'loans:appraise'],
    analyst: ['clients:read', 'loans:read', 'loans:analyze', 'payments:read', 'reports:read'],
    committee: ['clients:read', 'loans:read', 'loans:decide', 'payments:read', 'reports:read', 'branches:all'],
    cashier: ['clients:read', 'loans:read', 'payments:read', 'payments:post', 'cash:operate'],
    collector: ['clients:read', 'loans:read', 'payments:read', 'collections:read', 'collections:manage'],
    compliance: [...READ_ALL, 'compliance:read', 'compliance:manage', 'audit:read', 'reports:export'],
    auditor: [...READ_ALL, 'audit:read', 'compliance:read', 'reports:export', 'billing:read', 'accounting:read'],
};
/** Permisos de un rol, respetando lo que redefina la organización. */
export function permissionsForRole(role, overrides = {}) {
    return overrides[role] ?? DEFAULT_ROLE_PERMISSIONS[role];
}
/** Indica si el rol tiene el permiso, considerando lo que redefina la organización. */
export function hasPermission(role, permission, overrides = {}) {
    return permissionsForRole(role, overrides).includes(permission);
}
//# sourceMappingURL=permissions.js.map